
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box, Typography, Paper, Avatar, Divider, List, ListItem,
  ListItemAvatar, ListItemText, Button, Stack, Chip, alpha,
} from "@mui/material";
import {
  NotificationsActive, AccessTime, CheckCircle, ErrorOutline, Info,
  ArrowBack, ArrowForward, PersonOutline, MarkEmailRead,
  EventNote, MenuBook, Flag, Group,
} from "@mui/icons-material";
import TopBar from "./globalComponents/topbar";
import Footer from "./globalComponents/footer";

// ── Brand palette ────────────────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  secondaryDark: "#0A4A1C",
  secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  borderSoft: "#EEF1F6",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  warning: "#D97706",
  warningSoft: "#FEF3C7",
  error: "#DC2626",
  errorSoft: "#FEE2E2",
};

const BASE_URL = "https://backend-global-1vq9.onrender.com/api";

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatTime = (createdAt) => {
  if (!createdAt) return "";
  const now = new Date();
  const notifTime = new Date(createdAt);
  if (isNaN(notifTime.getTime())) return "";
  const diffMs = now - notifTime;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return notifTime.toLocaleDateString();
};

// Source-icon and color for the right-side decoration
const SOURCE_META = {
  connection: { icon: <Group sx={{ fontSize: 22 }} />,     color: C.primary },
  session:    { icon: <EventNote sx={{ fontSize: 22 }} />, color: C.primary },
  lesson:     { icon: <MenuBook sx={{ fontSize: 22 }} />,  color: C.primary },
  goal:       { icon: <Flag sx={{ fontSize: 22 }} />,      color: C.secondary },
};

// Status pill config — same colour story as PendingRequests / MentorRequests
const STATUS_CHIP = {
  accepted:    { bg: C.secondarySoft, fg: C.secondary, label: "Accepted" },
  rejected:    { bg: C.errorSoft,     fg: C.error,     label: "Declined" },
  pending:     { bg: C.warningSoft,   fg: C.warning,   label: "Pending" },
  upcoming:    { bg: C.primarySoft,   fg: C.primary,   label: "Upcoming" },
  published:   { bg: C.secondarySoft, fg: C.secondary, label: "Published" },
  completed:   { bg: C.secondarySoft, fg: C.secondary, label: "Completed" },
  in_progress: { bg: C.primarySoft,   fg: C.primary,   label: "In Progress" },
};

// ── Notification sources ────────────────────────────────────────────────────
// Each returns a normalized array. If a fetch fails, returns [] instead of
// throwing, so one broken source doesn't kill the whole list.

const fetchConnectionNotifications = async (userId, role) => {
  // Mentors get a "mentor" endpoint; learners get a "learner" endpoint
  // (NB: role in DB is "learner", not "mentee" — that was the original bug)
  const isMentor = role === "mentor";
  const path = isMentor ? "mentor" : "learner";

  try {
    const res = await axios.get(`${BASE_URL}/connection-requests/${path}/${userId}`);
    const requests = res.data?.data || [];

    return requests
      .filter((r) => ["pending", "accepted", "rejected"].includes(r.status))
      .map((req) => {
        const otherPerson = isMentor ? req.mentee : req.mentor;
        const otherName = otherPerson?.name || otherPerson?.fullName || (isMentor ? "A learner" : "A mentor");

        let message;
        let type;
        if (isMentor) {
          if (req.status === "pending")  { message = `New connection request from ${otherName}`; type = "info"; }
          if (req.status === "accepted") { message = `You accepted ${otherName}'s connection request`; type = "success"; }
          if (req.status === "rejected") { message = `You declined ${otherName}'s connection request`; type = "error"; }
        } else {
          // Learner side — don't surface "pending" since they sent it themselves
          if (req.status === "pending") return null;
          if (req.status === "accepted") { message = `${otherName} accepted your connection request 🎉`; type = "success"; }
          if (req.status === "rejected") { message = `${otherName} declined your connection request`; type = "error"; }
        }

        return {
          id: `conn-${req._id}`,
          source: "connection",
          message,
          type,
          status: req.status,
          time: formatTime(req.updatedAt || req.createdAt),
          timestamp: new Date(req.updatedAt || req.createdAt).getTime(),
          personName: otherName,
          personEmail: otherPerson?.email || "",
          personId: otherPerson?._id || otherPerson?.id || "",
          avatar: otherPerson?.avatar || "",
        };
      })
      .filter(Boolean);
  } catch (err) {
    console.error("Connection notifications failed:", err);
    return [];
  }
};

const fetchSessionNotifications = async (userId, role) => {
  try {
    // Mentors filter their own; learners see all
    const url = role === "mentor"
      ? `${BASE_URL}/sessions?mentorId=${userId}`
      : `${BASE_URL}/sessions`;
    const res = await axios.get(url);
    const sessions = res.data?.data || res.data || [];

    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;

    // Future sessions within the next 7 days
    return sessions
      .filter((s) => {
        if (!s.date) return false;
        const time = s.startTime || "00:00";
        const start = new Date(`${s.date}T${time}`).getTime();
        return !isNaN(start) && start > now && (start - now) < sevenDaysMs;
      })
      .map((s) => {
        const time = s.startTime || "00:00";
        const startMs = new Date(`${s.date}T${time}`).getTime();
        const hoursAway = Math.floor((startMs - now) / 3600000);

        let whenLabel;
        if (hoursAway < 24) whenLabel = `in ${Math.max(1, hoursAway)}h`;
        else whenLabel = `in ${Math.floor(hoursAway / 24)}d`;

        const message = role === "mentor"
          ? `Your "${s.title || "session"}" starts ${whenLabel}`
          : `Upcoming session "${s.title || "session"}" with ${s.mentorName || "a mentor"} ${whenLabel}`;

        return {
          id: `sess-${s._id}`,
          source: "session",
          message,
          type: "info",
          status: "upcoming",
          time: formatTime(s.createdAt),
          timestamp: startMs, // sort by when the session is, not when created
          personName: s.mentorName || "",
          personEmail: s.mentorEmail || "",
          personId: s.mentorId || "",
          avatar: "",
          extra: { platform: s.platform, link: s.meetingLink },
        };
      });
  } catch (err) {
    console.error("Session notifications failed:", err);
    return [];
  }
};

const fetchLessonNotifications = async (userId, role) => {
  // Only relevant for mentors right now — their own published lessons.
  // For learners we'd need an "assigned lessons" or "new from connected mentors"
  // endpoint that doesn't exist yet.
  if (role !== "mentor") return [];

  try {
    const res = await axios.get(`${BASE_URL}/lessons/all?mentorId=${userId}`);
    const lessons = res.data?.data || [];
    const cutoff = Date.now() - (30 * 24 * 60 * 60 * 1000); // last 30 days

    return lessons
      .filter((l) => l.isPublished && new Date(l.updatedAt || l.createdAt).getTime() > cutoff)
      .map((l) => ({
        id: `lesson-${l._id}`,
        source: "lesson",
        message: `Your lesson "${l.title}" is live and available to learners`,
        type: "success",
        status: "published",
        time: formatTime(l.updatedAt || l.createdAt),
        timestamp: new Date(l.updatedAt || l.createdAt).getTime(),
        personName: "",
        personEmail: "",
        personId: "",
        avatar: "",
      }));
  } catch (err) {
    console.error("Lesson notifications failed:", err);
    return [];
  }
};

const fetchGoalNotifications = async (userId, role) => {
  // Only relevant for learners — their own goal milestones
  if (role !== "learner") return [];

  try {
    const res = await axios.get(`${BASE_URL}/goals?learnerId=${userId}`);
    const goals = res.data?.data || res.data?.goals || res.data || [];

    return goals
      .filter((g) => g.status === "completed" || g.status === "in_progress")
      .map((g) => {
        const isDone = g.status === "completed";
        return {
          id: `goal-${g._id}`,
          source: "goal",
          message: isDone
            ? `You completed your goal: "${g.title}" 🎯`
            : `Keep going on "${g.title}" — ${g.progress || 0}% complete`,
          type: isDone ? "success" : "info",
          status: g.status,
          time: formatTime(g.updatedAt || g.createdAt),
          timestamp: new Date(g.updatedAt || g.createdAt).getTime(),
          personName: "",
          personEmail: "",
          personId: "",
          avatar: "",
        };
      });
  } catch (err) {
    // Goals endpoint might be different shape; fail quietly
    console.error("Goal notifications failed:", err);
    return [];
  }
};

// ── Main component ───────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const notificationsPerPage = 10;

  // Render the source-specific icon decoration
  const renderTypeIcon = (n) => {
    const meta = SOURCE_META[n.source] || SOURCE_META.connection;
    const bg = n.type === "success" ? C.secondarySoft
            : n.type === "error"   ? C.errorSoft
            : C.primarySoft;
    const fg = n.type === "success" ? C.secondary
            : n.type === "error"   ? C.error
            : meta.color;
    return (
      <Box sx={{
        width: 40, height: 40, borderRadius: "50%",
        bgcolor: bg, color: fg,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {React.cloneElement(meta.icon, { sx: { fontSize: 20 } })}
      </Box>
    );
  };

  // ── Fetch all sources in parallel and merge ──
  const fetchAllNotifications = useCallback(async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) return;

      const userId = user.id || user._id;
      const role = (user.role || "").toLowerCase();

      const [conn, sessions, lessons, goals] = await Promise.all([
        fetchConnectionNotifications(userId, role),
        fetchSessionNotifications(userId, role),
        fetchLessonNotifications(userId, role),
        fetchGoalNotifications(userId, role),
      ]);

      const merged = [...conn, ...sessions, ...lessons, ...goals]
        .sort((a, b) => b.timestamp - a.timestamp);

      setNotifications(merged);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllNotifications();
    // Poll every 60s. Was 15s — too aggressive for 4 parallel calls
    const interval = setInterval(fetchAllNotifications, 60_000);
    return () => clearInterval(interval);
  }, [fetchAllNotifications]);

  // ── Pagination ──
  const indexOfLast = currentPage * notificationsPerPage;
  const indexOfFirst = indexOfLast - notificationsPerPage;
  const currentNotifications = notifications.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(1, Math.ceil(notifications.length / notificationsPerPage));

  const handleNext = () => { if (currentPage < totalPages) setCurrentPage((p) => p + 1); };
  const handlePrev = () => { if (currentPage > 1) setCurrentPage((p) => p - 1); };

  // ── Counts for the hero pills ──
  const counts = {
    connection: notifications.filter(n => n.source === "connection").length,
    session:    notifications.filter(n => n.source === "session").length,
    lesson:     notifications.filter(n => n.source === "lesson").length,
    goal:       notifications.filter(n => n.source === "goal").length,
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", bgcolor: C.bg, minHeight: "100vh" }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 4, maxWidth: "1200px", mx: "auto", width: "100%" }}>

        {/* ── Hero header ────────────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden",
            background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            p: { xs: 3, sm: 4 },
            mb: { xs: 2.5, sm: 3.5 },
            color: "#fff",
            boxShadow: `0 6px 24px ${C.primary}30`,
          }}
        >
          <Box sx={{
            position: "absolute",
            top: "-40%", right: "-10%",
            width: { xs: 200, sm: 280 }, height: { xs: 200, sm: 280 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            pointerEvents: "none",
          }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
              <NotificationsActive sx={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }} />
              <Typography sx={{
                fontSize: { xs: 11, sm: 12 }, fontWeight: 700, letterSpacing: 1.5,
                textTransform: "uppercase", color: "rgba(255,255,255,0.75)",
              }}>
                Activity Feed
              </Typography>
            </Stack>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28, md: 32 },
              color: "#fff", lineHeight: 1.15, mb: 0.75, letterSpacing: -0.5,
            }}>
              Notifications
            </Typography>
            <Typography sx={{
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 13, sm: 14 }, maxWidth: 540, lineHeight: 1.5,
            }}>
              Connection requests, upcoming sessions, lessons, and goal milestones — all in one place.
            </Typography>

            {notifications.length > 0 && (
              <Stack direction="row" spacing={1} mt={2} flexWrap="wrap" useFlexGap>
                {counts.connection > 0 && (
                  <Chip
                    icon={<Group sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.connection} connection${counts.connection !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, "& .MuiChip-icon": { color: "#fff" } }}
                  />
                )}
                {counts.session > 0 && (
                  <Chip
                    icon={<EventNote sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.session} session${counts.session !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, "& .MuiChip-icon": { color: "#fff" } }}
                  />
                )}
                {counts.lesson > 0 && (
                  <Chip
                    icon={<MenuBook sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.lesson} lesson${counts.lesson !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{ bgcolor: "rgba(255,255,255,0.18)", color: "#fff", fontWeight: 700, "& .MuiChip-icon": { color: "#fff" } }}
                  />
                )}
                {counts.goal > 0 && (
                  <Chip
                    icon={<Flag sx={{ fontSize: "14px !important", color: "#fff !important" }} />}
                    label={`${counts.goal} goal${counts.goal !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      bgcolor: alpha(C.secondary, 0.85), color: "#fff", fontWeight: 700,
                      "& .MuiChip-icon": { color: "#fff" },
                    }}
                  />
                )}
              </Stack>
            )}
          </Box>
        </Paper>

        {/* ── Notifications card ─────────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: `1px solid ${C.border}`,
            bgcolor: C.paper,
            boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0, left: 0, right: 0,
              height: "3px",
              background: `linear-gradient(90deg, ${C.primary}, ${C.primary}88)`,
            },
          }}
        >
          {/* Section Header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} mt={0.5} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Typography variant="h6" fontWeight={700} sx={{ color: C.textPrimary, fontSize: { xs: 15, sm: 16 } }}>
                Recent Activity
              </Typography>
              <Chip
                label={notifications.length}
                size="small"
                sx={{
                  bgcolor: C.primarySoft, color: C.primary, fontWeight: 700,
                  height: 22, "& .MuiChip-label": { px: 1 },
                }}
              />
            </Stack>
          </Stack>

          <Divider sx={{ mb: 3, borderColor: C.borderSoft }} />

          {/* Notification List */}
          <List disablePadding>
            {loading && notifications.length === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: `0 8px 20px ${C.primary}30`,
                }}>
                  <NotificationsActive sx={{ fontSize: 36, color: "#fff" }} />
                </Box>
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8, px: 3, bgcolor: C.primarySofter, borderRadius: 3 }}>
                <Box sx={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  mx: "auto", mb: 3,
                  boxShadow: `0 8px 20px ${C.primary}30`,
                }}>
                  <MarkEmailRead sx={{ fontSize: 36, color: "#fff" }} />
                </Box>
                <Typography sx={{ color: C.textPrimary, fontWeight: 700, mb: 1, fontSize: 16 }}>
                  All Caught Up!
                </Typography>
                <Typography sx={{ color: C.textSecondary, fontSize: 13 }}>
                  You don't have any notifications yet.
                </Typography>
              </Box>
            ) : (
              <>
                {currentNotifications.map((n) => {
                  const initial = (n.personName || "?").charAt(0).toUpperCase();
                  const chip = STATUS_CHIP[n.status] || STATUS_CHIP.pending;
                  return (
                    <Paper
                      key={n.id}
                      elevation={0}
                      sx={{
                        mb: 1.5,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        border: `1px solid ${C.borderSoft}`,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: C.primary,
                          bgcolor: C.primarySofter,
                          transform: "translateX(2px)",
                          boxShadow: `0 4px 12px ${C.primary}10`,
                        },
                      }}
                    >
                      <ListItem disableGutters sx={{ flexDirection: "column", alignItems: "stretch", p: 0 }}>
                        <Box display="flex" alignItems="flex-start" width="100%" gap={2}>
                          {/* Person avatar OR source icon */}
                          {n.personName ? (
                            <ListItemAvatar sx={{ minWidth: 0 }}>
                              <Avatar
                                src={n.avatar}
                                sx={{
                                  width: 48, height: 48,
                                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                                  color: "#fff",
                                  fontWeight: 700, fontSize: "1.1rem",
                                  boxShadow: `0 4px 10px ${C.primary}25`,
                                }}
                              >
                                {!n.avatar && initial}
                              </Avatar>
                            </ListItemAvatar>
                          ) : (
                            renderTypeIcon(n)
                          )}

                          <Box flex={1} minWidth={0}>
                            <Typography sx={{
                              fontWeight: 700, fontSize: { xs: 13.5, sm: 14.5 },
                              color: C.textPrimary, lineHeight: 1.5, mb: 0.75,
                            }}>
                              {n.message}
                            </Typography>

                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap gap={0.5}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <AccessTime sx={{ fontSize: 14, color: C.textMuted }} />
                                <Typography sx={{
                                  color: C.textMuted, fontWeight: 600, fontSize: "0.74rem",
                                }}>
                                  {n.time}
                                </Typography>
                              </Stack>
                              {chip && (
                                <Chip
                                  label={chip.label}
                                  size="small"
                                  sx={{
                                    height: 20, fontSize: "0.68rem", fontWeight: 700,
                                    bgcolor: chip.bg, color: chip.fg,
                                    "& .MuiChip-label": { px: 0.75 },
                                  }}
                                />
                              )}
                              <Chip
                                label={n.source}
                                size="small"
                                sx={{
                                  height: 20, fontSize: "0.66rem", fontWeight: 700,
                                  bgcolor: C.borderSoft, color: C.textSecondary,
                                  textTransform: "capitalize",
                                  "& .MuiChip-label": { px: 0.75 },
                                }}
                              />
                            </Stack>

                            {/* Contact details — only if we have a person */}
                            {n.personName && n.personEmail && (
                              <Box sx={{
                                mt: 1.5, p: 1.5,
                                bgcolor: C.primarySofter, borderRadius: 2,
                                border: `1px solid ${C.primarySoft}`,
                              }}>
                                <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                                  <PersonOutline sx={{ fontSize: 14, color: C.primary }} />
                                  <Typography sx={{
                                    color: C.textMuted, fontWeight: 700, fontSize: "0.7rem",
                                    textTransform: "uppercase", letterSpacing: 0.5,
                                  }}>
                                    Contact
                                  </Typography>
                                </Stack>
                                <Typography sx={{ fontSize: "0.75rem", color: C.textPrimary, mb: 0.25 }}>
                                  <Box component="span" sx={{ fontWeight: 700 }}>Name:</Box> {n.personName}
                                </Typography>
                                <Typography sx={{ fontSize: "0.75rem", color: C.textPrimary }}>
                                  <Box component="span" sx={{ fontWeight: 700 }}>Email:</Box> {n.personEmail}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Right-edge type icon — only when an avatar is also shown */}
                          {n.personName && (
                            <Box sx={{ flexShrink: 0 }}>
                              {renderTypeIcon(n)}
                            </Box>
                          )}
                        </Box>
                      </ListItem>
                    </Paper>
                  );
                })}

                {/* Pagination */}
                {notifications.length > notificationsPerPage && (
                  <Box sx={{ mt: 3, pt: 2.5, borderTop: `1px solid ${C.borderSoft}` }}>
                    <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                      <Button
                        startIcon={<ArrowBack />}
                        onClick={handlePrev}
                        disabled={currentPage === 1}
                        sx={{
                          textTransform: "none", fontWeight: 700,
                          color: currentPage === 1 ? C.textMuted : C.primary,
                          "&:hover": { bgcolor: currentPage === 1 ? "transparent" : C.primarySoft },
                        }}
                      >
                        Previous
                      </Button>
                      <Typography sx={{ fontWeight: 700, color: C.textSecondary, fontSize: 13 }}>
                        Page {currentPage} of {totalPages}
                      </Typography>
                      <Button
                        endIcon={<ArrowForward />}
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        sx={{
                          textTransform: "none", fontWeight: 700,
                          color: currentPage === totalPages ? C.textMuted : C.primary,
                          "&:hover": { bgcolor: currentPage === totalPages ? "transparent" : C.primarySoft },
                        }}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </List>
        </Paper>
      </Box>

      <Footer />
    </Box>
  );
}


